import React, { Dispatch, useEffect, useMemo, useState } from 'react';

import {
    PlayTypeNameEnum,
    PlayTypes,
    ProgramTypeEnum,
    ProgramTypeNameEnum,
    ProgramTypesList,
    ServiceLaunch
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import alertErrorTokens from '@amzn/meridian-tokens/base/icon/alert';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import { CellClassRules, ColumnState, GridApi } from 'ag-grid-community';
import bigDecimal from 'js-big-decimal';
import { cloneDeep, isEmpty, isUndefined } from 'lodash';
import { TerritoryConfigurator } from 'src/components/AddPrimeAMFRateCard/AddCoreRates/TerritoryConfigurator/TerritoryConfigurator';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { showPlayCustomerProgramTypeCols } from 'src/components/ViewPrimeAMFRateCard/ViewCoreRates/cppMauColDefs';
import { MISSING_REQUIRED_VALUES } from 'src/constants/errorMessages';
import { useGridCopyPasteHandler } from 'src/hooks/useGridCopyPasteHandler';
import { IMetadata } from 'src/types/IMetadata';
import { IPrimeAMFRateCardValidationAction } from 'src/types/IPrimeAMFRateCardValidationAction';
import { IPrimeAMFRateCardValuesAction } from 'src/types/IPrimeAMFRateCardValuesAction';
import { IPrimeAMFRateCardValues, IRateCardGridRowData } from 'src/types/IRateCard';
import { getCamelStrFromAllCaps, isNumberOrNumberString } from 'src/util/RateCards';

import { IPrimeAMFRateCardErrors, generateGridErrorId } from '../../rateCardValidationReducer';
import { getCppMauColDefs } from '../cppMauColDefs';

interface IAddCppGridProps {
    rateCardValues: IPrimeAMFRateCardValues;
    dispatchRateCardValues: Dispatch<IPrimeAMFRateCardValuesAction>;
    rateCardErrors: IPrimeAMFRateCardErrors;
    dispatchRateCardErrors: Dispatch<IPrimeAMFRateCardValidationAction>;
    filterText: string;
    setCppGridApi: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
    augmentedMetadata: IMetadata;
}

export const AddCppGrid = ({
    rateCardValues,
    dispatchRateCardValues,
    rateCardErrors,
    dispatchRateCardErrors,
    filterText,
    setCppGridApi,
    augmentedMetadata
}: IAddCppGridProps) => {
    const {
        internalData: internalCppRates,
        setInternalData: setInternalCppRates,
        isPasting,
        onPasteStart,
        onPasteEnd
    } = useGridCopyPasteHandler(rateCardValues.rateCardRowData.cpp);

    const services = augmentedMetadata.serviceMapping;
    const isPrimeRateCard = rateCardValues.serviceId === services[2].serviceId;
    const CAEs = Object.values(augmentedMetadata.CaeMapping!);
    const [launchedTerritoryList, setLaunchedTerritoryList] = useState<ServiceLaunch[]>([]);
    const [filteredTerritoryIdList, setFilteredTerritoryIdList] = useState<Set<number>>(new Set());

    // hold on to row data which has been filtered in case the user wants to re-add the territory
    const [rejectedTerritoryRowData, setRejectedTerritoryRowData] = useState<IRateCardGridRowData[]>([]);

    const conversionModels = isPrimeRateCard
        ? (rateCardValues.primeContractTerms?.costPerPlay?.conversions ?? [])
        : (rateCardValues.amfContractTerms?.adRevShare.conversions ?? []);
    const cellClassRules = useMemo<CellClassRules>(
        () => ({
            'error-cell': (params) =>
                rateCardErrors.rateCardRowData.cpp.has(
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    generateGridErrorId(Number(params.node.id), params.colDef.field!)
                )
        }),
        [rateCardErrors.rateCardRowData.cpp]
    );
    const colDefs = getCppMauColDefs(augmentedMetadata, conversionModels).map((colDef) => ({
        ...colDef,
        cellClassRules
    }));

    const sortModel: ColumnState[] = [
        { colId: 'playType', sort: 'asc', sortIndex: 0 },
        { colId: 'programType', sort: 'asc', sortIndex: 1 },
        { colId: 'territory', sort: 'asc', sortIndex: 2 }
    ];

    useEffect(() => {
        const idList = augmentedMetadata.activeServiceLaunchDateMapping![rateCardValues.serviceId];
        setLaunchedTerritoryList(idList);
    }, [augmentedMetadata.activeServiceLaunchDateMapping]);

    useEffect(() => {
        if (launchedTerritoryList.length === 0) {
            // probably not loaded yet
            return;
        }

        // use launched territories to initialize base row data for each territory
        const cppRateBaseRowData = launchedTerritoryList.map(({ territoryID: territoryId }) => ({
            territoryId: territoryId!,
            rateCardCurrency: augmentedMetadata.territoryMapping[territoryId!].currency ?? '',
            settlementCurrency: augmentedMetadata.territoryMapping[territoryId!].settlementCurrency ?? '',
            cae:
                CAEs.find((CAE) => CAE.territoryID === territoryId && CAE.serviceID === rateCardValues.serviceId)
                    ?.contentAcquisitionEntityCode ?? '',
            playType: '',
            playTypeId: '',
            playTypeDefinitionId: '',
            programType: '',
            rate: ''
        }));

        // get and filter play types based on the service (Prime or AMF)
        let playTypes = isPrimeRateCard
            ? rateCardValues.primeContractTerms?.costPerPlay?.playTypes
            : rateCardValues.amfContractTerms?.adRevShare.playTypes;
        playTypes = playTypes?.filter((playType) => playType.playTypeName !== PlayTypeNameEnum.STANDARD);

        const programTypes = rateCardValues.primeContractTerms?.costPerPlay?.programTypeList;

        // generate new combinations based on play types and program types
        const newCppRateRowData = generateRowDataCombinations(cppRateBaseRowData, playTypes ?? [], programTypes ?? []);

        // merge new combinations with existing row data to preserve user input
        const mergedCppRateRowData = mergeWithPreviousRowData(newCppRateRowData, [
            ...internalCppRates,
            ...rejectedTerritoryRowData
        ]);

        const accepted = mergedCppRateRowData.filter((row) => {
            if (
                row.programType === ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY &&
                !rateCardValues.primeContractTerms?.costPerPlay?.householdSharingTerritoryIds!.has(row.territoryId)
            ) {
                return false;
            }

            return !filteredTerritoryIdList.has(row.territoryId);
        });
        const rejected = mergedCppRateRowData.filter((row) => filteredTerritoryIdList.has(row.territoryId));

        setInternalCppRates(accepted);
        setRejectedTerritoryRowData(rejected);
    }, [
        launchedTerritoryList,
        filteredTerritoryIdList,
        rateCardValues.primeContractTerms?.costPerPlay?.programTypeList
    ]);

    // generate combinations of row data based on play types and program types.
    const generateRowDataCombinations = (
        baseRowData: IRateCardGridRowData[],
        playTypes: PlayTypes,
        programTypes: ProgramTypesList
    ): IRateCardGridRowData[] => {
        let newRowData = baseRowData;

        // For India territories, create only one row with Default program type
        const indiaRows = newRowData.filter(
            (row) => augmentedMetadata.territoryMapping[row.territoryId]?.territoryCode === 'IN'
        );
        const nonIndiaRows = newRowData.filter(
            (row) => augmentedMetadata.territoryMapping[row.territoryId]?.territoryCode !== 'IN'
        );

        // Generate combinations for non-India territories
        let nonIndiaRowData = nonIndiaRows;
        if (!isEmpty(playTypes)) {
            nonIndiaRowData = playTypes.flatMap((playType) =>
                nonIndiaRows.map((row) => ({
                    ...row,
                    playType: playType.playTypeText ?? '',
                    playTypeId: playType.playTypeId ?? '',
                    playTypeDefinitionId: playType.playTypeDefinitions?.[0]?.playTypeDefinitionId ?? ''
                }))
            );
        }

        // For India, create single row with Default program type
        const indiaRowData = indiaRows.map((row) => ({
            ...row,
            playType: PlayTypeNameEnum.STANDARD,
            playTypeId: 1,
            playTypeDefinitionId: '',
            programType: ProgramTypeEnum.DEFAULT
        }));

        newRowData = [...nonIndiaRowData, ...indiaRowData];

        // generate combinations for program types
        const clonedProgramTypes = cloneDeep(programTypes);
        // Add default program type if not present in program type list
        if (
            !isEmpty(clonedProgramTypes) &&
            isUndefined(
                clonedProgramTypes.find((programType) => programType.programTypeName === ProgramTypeEnum.DEFAULT)
            )
        ) {
            clonedProgramTypes.push({
                programTypeName: ProgramTypeEnum.DEFAULT
            });
        }

        // Apply program type combinations only to non-India territories
        const finalIndiaRows = newRowData.filter(
            (row) => augmentedMetadata.territoryMapping[row.territoryId]?.territoryCode === 'IN'
        );
        const finalNonIndiaRows = newRowData.filter(
            (row) => augmentedMetadata.territoryMapping[row.territoryId]?.territoryCode !== 'IN'
        );

        let processedNonIndiaRows = finalNonIndiaRows;
        if (!isEmpty(clonedProgramTypes)) {
            processedNonIndiaRows = clonedProgramTypes.flatMap((programType) => {
                // only attach DE_RADIO_PLAY for On Demand row
                if (programType.programTypeName === ProgramTypeEnum.DE_RADIO_PLAY) {
                    return finalNonIndiaRows
                        .filter((row) => row.playType === getCamelStrFromAllCaps(PlayTypeNameEnum.ON_DEMAND))
                        .map((row) => ({
                            ...row,
                            programType: programType.programTypeName!
                        }));
                }

                // Only attach HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY for on-demand and shuffle
                else if (programType.programTypeName === ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY) {
                    return finalNonIndiaRows
                        .filter((row) =>
                            [
                                getCamelStrFromAllCaps(PlayTypeNameEnum.ON_DEMAND),
                                getCamelStrFromAllCaps(PlayTypeNameEnum.SHUFFLE)
                            ].some((playType) => row.playType === playType)
                        )
                        .map((row) => ({
                            ...row,
                            programType: programType.programTypeName!
                        }));
                } else {
                    return finalNonIndiaRows.map((row) => ({
                        ...row,
                        programType: programType.programTypeName!
                    }));
                }
            });
        }

        newRowData = [...processedNonIndiaRows, ...finalIndiaRows];

        return newRowData;
    };

    // merge newly generated row data combinations with existing data, preserving user input
    const mergeWithPreviousRowData = (
        newRowData: IRateCardGridRowData[],
        prevRowData: IRateCardGridRowData[]
    ): IRateCardGridRowData[] => {
        const thresholdColIds = colDefs
            .filter((colDef) => colDef.field!.startsWith('threshold'))
            .map((colDef) => colDef.field);

        const householdSharingProgramType = rateCardValues.primeContractTerms?.costPerPlay?.programTypeList?.find(
            (programType) => programType.programTypeName === ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY
        );
        const householdSharingMultiplier = isUndefined(householdSharingProgramType)
            ? undefined
            : householdSharingProgramType.programTypeDefinitions![0].value!;

        return newRowData.map((newRow) => {
            let rowToAdd = newRow;

            const matchedRow = prevRowData.find(
                (prevRow) =>
                    prevRow.territoryId === newRow.territoryId &&
                    prevRow.playType === newRow.playType &&
                    (prevRow.programType === newRow.programType ||
                        (prevRow.programType === ProgramTypeEnum.DEFAULT && isEmpty(newRow.programType)))
            );

            const hasSameThresholdCols = isEmpty(thresholdColIds)
                ? true
                : thresholdColIds.every((key) => key! in (matchedRow ?? {}));

            // preserve rates for DE Radio Play row that doesn't require threshold rates
            const isOnDemandDERadioPlayRow =
                matchedRow?.playType === getCamelStrFromAllCaps(PlayTypeNameEnum.ON_DEMAND) &&
                matchedRow?.programType === ProgramTypeEnum.DE_RADIO_PLAY;

            // preserve rates for JP row that doesn't always require threshold rates
            const isJPRowWithThresholds =
                augmentedMetadata.territoryMapping[matchedRow?.territoryId ?? -1]?.territoryCode === 'JP';

            // Determine if new row is household sharing
            const isHouseholdSharingPlayRow =
                [
                    getCamelStrFromAllCaps(PlayTypeNameEnum.ON_DEMAND),
                    getCamelStrFromAllCaps(PlayTypeNameEnum.SHUFFLE)
                ].includes(newRow.playType as PlayTypeNameEnum) &&
                newRow.programType === ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY;

            // Generate rates for Household Sharing
            if (isHouseholdSharingPlayRow) {
                // Generate rates from the DEFAULT program type
                const defaultProgramTypeMatchedRow = prevRowData.find(
                    (prevRow) =>
                        prevRow.territoryId === newRow.territoryId &&
                        prevRow.playType === newRow.playType &&
                        prevRow.programType === ProgramTypeNameEnum.DEFAULT
                );

                if (!isUndefined(defaultProgramTypeMatchedRow)) {
                    rowToAdd = cloneDeep(defaultProgramTypeMatchedRow);
                    rowToAdd = {
                        ...rowToAdd,
                        playTypeId: newRow.playTypeId,
                        playTypeDefinitionId: newRow.playTypeDefinitionId,
                        programType: newRow.programType
                    };

                    ['rate', ...thresholdColIds]
                        .filter((colId) => !isUndefined(colId))
                        .forEach((colId) => {
                            if (isNumberOrNumberString(rowToAdd![colId!])) {
                                rowToAdd = {
                                    ...rowToAdd!,
                                    [colId as string]: multiplyRate(
                                        rowToAdd![colId!],
                                        householdSharingMultiplier ?? '1.0'
                                    )
                                };
                            }
                        });
                }
            }

            return matchedRow &&
                !isHouseholdSharingPlayRow &&
                (hasSameThresholdCols || isOnDemandDERadioPlayRow || isJPRowWithThresholds)
                ? { ...matchedRow, playTypeId: newRow.playTypeId, playTypeDefinitionId: newRow.playTypeDefinitionId }
                : rowToAdd;
        });
    };

    // Sync internalCppRates with parent rateCardValues and validate
    useEffect(() => {
        // Sync and validate after pasting operation is complete
        if (isPasting) {
            return;
        }

        calculateHouseholdSharingRates();

        syncCppRatesState();
    }, [internalCppRates, isPasting]);

    const onCellValueChanged = () => {
        // Ignore syncing and validating if pasting multiple cells at once
        if (isPasting) {
            return;
        }

        calculateHouseholdSharingRates();

        syncCppRatesState();
    };

    const syncCppRatesState = () => {
        dispatchRateCardValues({
            type: 'set_cpp_grid_row_data',
            rowData: internalCppRates
        });
    };

    // If default program type rates are modified, then modify household sharing rates as well
    const calculateHouseholdSharingRates = () => {
        const householdSharingProgramType = rateCardValues.primeContractTerms?.costPerPlay?.programTypeList?.find(
            (programType) => programType.programTypeName === ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY
        );

        if (isUndefined(householdSharingProgramType)) {
            return;
        }

        let isRowDataEqual = true;
        const householdSharingMultiplier = householdSharingProgramType.programTypeDefinitions![0].value!;

        // Fetch default rates into map
        const defaultRatesMap = internalCppRates.reduce(
            (accuMap, currentRow) => {
                if (
                    [
                        getCamelStrFromAllCaps(PlayTypeNameEnum.ON_DEMAND),
                        getCamelStrFromAllCaps(PlayTypeNameEnum.SHUFFLE)
                    ].includes(currentRow.playType as PlayTypeNameEnum) &&
                    currentRow.programType === ProgramTypeNameEnum.DEFAULT
                ) {
                    return {
                        ...accuMap,
                        [`${currentRow.territoryId}-${currentRow.playType}`]: currentRow
                    };
                } else {
                    return accuMap;
                }
            },
            {} as Record<string, IRateCardGridRowData>
        );

        // Replace household sharing rows with newly calculated rates
        const newRowData = internalCppRates.map((row) => {
            if (
                ![
                    getCamelStrFromAllCaps(PlayTypeNameEnum.ON_DEMAND),
                    getCamelStrFromAllCaps(PlayTypeNameEnum.SHUFFLE)
                ].includes(row.playType as PlayTypeNameEnum) ||
                row.programType !== ProgramTypeNameEnum.HOUSEHOLD_SHARING_SIMULTANEOUS_PLAY
            ) {
                return row;
            }

            let rowToAdd = cloneDeep(row);
            const thresholdColIds = colDefs
                .filter((colDef) => colDef.field!.startsWith('threshold'))
                .map((colDef) => colDef.field);

            ['rate', ...thresholdColIds]
                .filter((colId) => !isUndefined(colId))
                .forEach((colId) => {
                    if (isNumberOrNumberString(rowToAdd[colId!])) {
                        const newRateValue = multiplyRate(
                            defaultRatesMap[`${row.territoryId}-${row.playType}`]?.[colId!] as string,
                            householdSharingMultiplier ?? '1.0'
                        );

                        // Only replace value if it is different from the previous value
                        if (newRateValue !== rowToAdd[colId!]) {
                            isRowDataEqual = false;
                            rowToAdd = {
                                ...rowToAdd!,
                                [colId as string]: newRateValue
                            };
                        }
                    }
                });

            return rowToAdd;
        });

        if (!isRowDataEqual) {
            setInternalCppRates(newRowData);
        }
    };

    const multiplyRate = (oldRate: string | number, multiplier: string): string =>
        new bigDecimal(oldRate)
            .multiply(new bigDecimal(multiplier))
            .round(4, bigDecimal.RoundingModes.HALF_UP)
            .stripTrailingZero()
            .getValue();

    // Validate
    useEffect(() => {
        dispatchRateCardErrors({
            type: 'validate_cpp_rates',
            rateCardValues,
            visibleColumns: colDefs.filter((col) => !col.hide),
            metadata: augmentedMetadata
        });
    }, [rateCardValues.rateCardRowData]);

    showPlayCustomerProgramTypeCols(internalCppRates, colDefs);

    return (
        <Column spacing="400" heights={['fit', 'fill']}>
            <Row widths={['fit', 'fill']}>
                <Row>
                    <Heading level={3}>Cost per play</Heading>
                    {!isEmpty(rateCardErrors.rateCardRowData.cpp) && (
                        <Tooltip position={'top'} title={MISSING_REQUIRED_VALUES}>
                            <Box type="fill" spacingInset="200 300">
                                <Row spacing="300">
                                    <Icon tokens={alertErrorTokens} />
                                    <Text type="h100" data-testid="error-row-count">
                                        {rateCardErrors.rateCardRowData.cpp.size}
                                    </Text>
                                    <Text>invalid</Text>
                                </Row>
                            </Box>
                        </Tooltip>
                    )}
                </Row>
                <Row alignmentHorizontal={'end'}>
                    <TerritoryConfigurator
                        helperText={'Add/Remove Territories'}
                        launchedTerritoryList={launchedTerritoryList}
                        features={[
                            {
                                featureName: 'Enabled for Cost per play',
                                filteredTerritoryIdList,
                                setFilteredTerritoryIdList
                            }
                        ]}
                    />
                </Row>
            </Row>
            <Column minHeight={400}>
                <BaseAgGrid
                    columnDefs={colDefs}
                    rowData={internalCppRates}
                    sortModel={sortModel}
                    onCellValueChanged={onCellValueChanged}
                    suppressRowClickSelection={true}
                    suppressSizeColumnsToFit={true}
                    enableRangeSelection={true}
                    singleClickEdit={false}
                    onPasteStart={onPasteStart}
                    onPasteEnd={onPasteEnd}
                    filterText={filterText}
                    setAgGridApi={setCppGridApi}
                />
            </Column>
        </Column>
    );
};
